import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { iLanguageCertificate } from "@/types/models";
import { notify } from "../utils/toast";

export interface LanguageCertificate {
	id: string;
	certificateNumber: string; // Số chứng chỉ
	cccd: string; // CCCD
	studentId?: string; // Mã sinh viên (optional)
	studentName: string; // Họ và tên
	fullName?: string; // Họ và tên
	dateOfBirth: string; // Ngày sinh
	placeOfBirth: string; // Nơi sinh
	gender: string; // Giới tính
	ethnicCode: string; // Mã Dân tộc
	ethnicity: string; // Dân tộc
	nationalityCode: string; // Mã quốc tịch
	nationality: string; // quốc tịch
	language: string; // Ngôn ngữ
	languageCode: string; // Mã ngôn ngữ
	level: string; // Trình độ 
	examDate: string; // Ngày thi
	issueDate: string; // Ngày cấp
	expiryDate?: string; // Ngày hết hạn (optional)
	examCenter: string; // Trung tâm thi
	totalScore?: number; // Tổng điểm
	listeningScore?: number; // Điểm nghe
	speakingScore?: number; // Điểm nói
	readingScore?: number; // Điểm đọc
	writingScore?: number; // Điểm viết
	certificateType: string; // Loại chứng chỉ (TOEIC, IELTS, TOEFL, etc.)
	certificateTypeId: string; // ID loại chứng chỉ
	signedBy: string; // Người ký
	signerTitle: string; // Chức danh người ký
	signDate?: string; // Ngày ký
	signedDate?: string; // Ngày ký
	notes?: string; // Ghi chú
	registrationNumber: string;
	decisionToEstablishCouncilNumber: string;
	recognitionDecisionNumber: string;
	course: string;
	courseStart?: Date;
	courseEnd?: Date;
	status: "Nội bộ" | "Đang gửi" | "Đã xác nhận" | "Đã thu hồi"; // Trạng thái
	createdAt?: string;
	updatedAt?: string;
	requestTypeId: string;
	requestType: string;
}

export interface LanguageCertificateFilters {
	search?: string;
	language?: string;
	certificateType?: string;
	requestType?: string;
	level?: string;
	courseStart?: string;
	courseEnd?: string;
	status?: "Nội bộ" | "Đang gửi" | "Đã xác nhận" | "Đã thu hồi";
	page?: number;
	limit?: number;
}

export interface LanguageCertificateListResponse {
	data: LanguageCertificate[];
	total: number;
	page: number;
	totalPages: number;
	limit: number;
}

export interface BulkImportResponse {
	created: LanguageCertificate[];
	count: number;
	errors?: { row: number; message: string }[];
}


export const languageCertificatesApi = {
	count: async ()=> {
		return await apiClient.get<number>(`${config.endpoints.languageCertificates}/count`);
	},
	getAll: async (filters?: LanguageCertificateFilters): Promise<ApiResponse<LanguageCertificateListResponse>> => {
		try {
			const response = await apiClient.get<LanguageCertificateListResponse>(config.endpoints.languageCertificates, filters);
			return response;
		} catch (error) {
			console.error("Error fetching language certificates:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw new Error("Lỗi khi lấy danh sách chứng chỉ ngoại ngữ");
		}
	},

	// Get language certificate by ID
	getById: async (id: string): Promise<ApiResponse<LanguageCertificate>> => {
		try {
			const response = await apiClient.get<LanguageCertificate>(`${config.endpoints.languageCertificates}/${id}`);
			return response;
		} catch (error) {
			console.error("Error fetching language certificate:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Create new language certificate
	create: async (certificate: Omit<iLanguageCertificate, "id" | "createdAt" | "updatedAt">): Promise<ApiResponse<iLanguageCertificate>> => {
		try {
			const response = await apiClient.post<iLanguageCertificate>(config.endpoints.languageCertificates, certificate);
			return response;
		} catch (error) {
			console.error("Error creating language certificate:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Update language certificate
	update: async (id: string, certificate: Partial<LanguageCertificate>): Promise<ApiResponse<LanguageCertificate>> => {
		try {
			const response = await apiClient.put<LanguageCertificate>(`${config.endpoints.languageCertificates}/${id}`, certificate);
			return response;
		} catch (error) {
			console.error("Error updating language certificate:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Delete language certificate
	delete: async (id: string): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.delete<void>(`${config.endpoints.languageCertificates}/${id}`);
			return response;
		} catch (error) {
			console.error("Error deleting language certificate:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	// Bulk import language certificates
	bulkImport: async (certificates: Omit<LanguageCertificate, "id" | "createdAt" | "updatedAt">[]): Promise<ApiResponse<BulkImportResponse>> => {
		try {
			const response = await apiClient.post<BulkImportResponse>(`${config.endpoints.languageCertificates}/bulk-import`, certificates);
			return response;
		} catch (error) {
			console.error("Error bulk importing language certificates:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw new Error("Lỗi khi nhập khẩu chứng chỉ ngoại ngữ hàng loạt");
		}
	},

	// Export language certificates
	export: async (filters?: LanguageCertificateFilters): Promise<ApiResponse<LanguageCertificate[]>> => {
		try {
			const response = await apiClient.get<LanguageCertificate[]>(`${config.endpoints.languageCertificates}/export`, filters);
			return response;
		} catch (error) {
			console.error("Error exporting language certificates:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw new Error("Lỗi khi xuất chứng chỉ ngoại ngữ");
		}
	},

	// Search public language certificates (for public search page)
	searchPublic: async (query: { cccd?: string; studentId?: string; certificateNumber?: string }): Promise<ApiResponse<LanguageCertificate[]>> => {
		try {
			const response = await apiClient.get<LanguageCertificate[]>(`${config.endpoints.public.search}/language-certificates`, query);
			return response;
		} catch (error) {
			console.error("Error searching language certificates:", error);
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},
	checkExist: async ({ searchValue, field }: { searchValue: string; field: "degreeNumber" }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.languageCertificates}/check-exist`, { search_value: searchValue, field });
		return response;
	},
};
